import {
  LngLatBounds,
  type LngLatLike,
  type Map as MaplibreMap
} from "maplibre-gl";
import type {
  Feature,
  FeatureCollection,
  Geometry,
  MultiPolygon,
  Point,
  Position
} from "geojson";

import { COLORS } from "@/constants/colors";
import { FORMAT_CONFIG } from "@/constants/format";
import {
  getNextNumberSafeFormatted,
  roundUpDecimal
} from "@/helpers/numberHelpers";
import {
  CategoryColorCategory,
  CategoryRange,
  PrimitiveDisplayValue
} from "@/types/common";
import { ColorRanges, DataSchemaType, EColorNames } from "@/types/dataset";

import { getColorScaleMap } from "./color";
import { isOrdinalType } from "./dataset";
import { formatNumber } from "./number";

/**
 * Enhances polygon data with additional properties like area and metadata
 * @param polygonData - The polygon feature to enhance
 * @returns Enhanced polygon feature with additional properties
 */
export const enhancePolygonData = (
  polygonData: Feature<Geometry>
): Feature<Geometry> => {
  return {
    ...polygonData,
    properties: {
      ...polygonData.properties,
      name: polygonData.properties?.name || "Unnamed Area",
      type: "Polygon Region",
      lastUpdated: new Date().toLocaleDateString()
    }
  };
};

/**
 * Enhances point data with additional properties and metadata
 */
export const enhancePointsData = (
  pointsData: FeatureCollection<Point>
): FeatureCollection<Point> => {
  return {
    ...pointsData,
    features: pointsData.features.map((feature, index) => ({
      ...feature,
      id: feature.id ?? index,
      properties: {
        ...feature.properties,
        id: feature.id ?? index,
        name: feature.properties?.name || `Point ${index + 1}`,
        type: feature.properties?.type || "Location Point",
        coordinates: `${feature.geometry.coordinates[1].toFixed(4)}, ${feature.geometry.coordinates[0].toFixed(4)}`,
        category: feature.properties?.category || "Default",
        lastUpdated: new Date().toLocaleDateString()
      }
    }))
  };
};

/**
 * Validates if polygon data meets all requirements for creating a polygon layer
 */
export const isValidPolygonData = (
  map: MaplibreMap | null,
  polygonData: FeatureCollection<Geometry>,
  visualizationOrder: string[],
  datasetId: string
): boolean => {
  return !!(
    map &&
    polygonData &&
    polygonData.features.length > 0 &&
    !visualizationOrder.some((layer) => layer === datasetId)
  );
};

/** Values the Ordinal legend hides as "no data". Callers that need the real
 * distinct values opt out via `dropOrdinalSentinels: false`. */
const ORDINAL_SENTINEL_VALUES = new Set(["0", " "]);

/**
 * Handles color distribution for string/categorical values
 */
export const handleCategoricalValues = (
  propertyValues: (string | boolean | null | undefined)[],
  step: number,
  { dropOrdinalSentinels = true }: { dropOrdinalSentinels?: boolean } = {}
): CategoryColorCategory[] => {
  const mappedValues = [
    ...new Set(
      propertyValues
        .filter(
          (v): v is string | boolean =>
            typeof v === "string" || typeof v === "boolean"
        )
        .map((v) => (typeof v === "boolean" ? (v ? "true" : "false") : v))
    )
  ].filter(
    (category) =>
      !dropOrdinalSentinels || !ORDINAL_SENTINEL_VALUES.has(category)
  );

  const compareAlphabetically = (a: string, b: string) => a.localeCompare(b);

  const sliceLimit = Math.min(mappedValues.length, step);
  const hasEmpty = mappedValues.includes("");
  const uniqueCategories = [...new Set(mappedValues.filter((v) => v !== ""))]
    .slice(0, sliceLimit)
    .sort(compareAlphabetically);

  if (hasEmpty) uniqueCategories.push("");

  return uniqueCategories.map((category, index) => ({
    category,
    color: index
  }));
};

/**
 * Handles color distribution for numeric values using quantiles
 * This function calculates statistical quantiles from a set of numeric values
 * to create appropriate breakpoints for data visualization
 *
 * @param propertyValues - Array of numeric values to analyze
 * @returns Array of breakpoints that divide the data into equal-sized groups
 */
export const handleNumericValues = (
  propertyValues: (number | null | undefined | string)[],
  step: number
): (number | null)[] => {
  if (!propertyValues || propertyValues.length === 0) return [];
  const stepValue = step + 1;

  const sortedNumericValues = [...new Set(propertyValues)]
    .filter((v): v is number => typeof v === "number" && !Number.isNaN(v))
    .sort((a, b) => b - a);

  const valueNumericCount = sortedNumericValues.length;

  const hasNulls = propertyValues.some(
    (value) => value === null || value === undefined || value === ""
  );

  if (valueNumericCount === 0) return hasNulls ? [null] : [];

  if (valueNumericCount === 1) {
    const result: (number | null)[] = new Array(stepValue).fill(
      sortedNumericValues[0]
    );
    if (hasNulls) result.push(null);
    return result;
  }

  // If we don't have enough data points to calculate full quantiles,
  // simply return the distinct values (preserving descending order).
  // This threshold is based on the TOTAL number of numeric entries, not the
  // number of unique values. This ensures datasets with many duplicates but
  // sufficient overall length still produce the full quantile array.

  if (valueNumericCount < stepValue) {
    const result: (number | null)[] = [...sortedNumericValues];
    if (hasNulls) result.push(null);
    return result;
  }

  const quantiles: (number | null)[] = Array.from(
    { length: stepValue },
    (_, i) => {
      const position = (i / (stepValue - 1)) * (valueNumericCount - 1);
      const lowerIndex = Math.floor(position);
      const upperIndex = Math.ceil(position);

      if (lowerIndex === upperIndex) {
        return sortedNumericValues[lowerIndex];
      }

      const weight = position - lowerIndex;
      return (
        (1 - weight) * sortedNumericValues[lowerIndex] +
        weight * sortedNumericValues[upperIndex]
      );
    }
  );

  if (hasNulls) quantiles.push(null);

  return quantiles;
};

/**
 * Calculates the area of a MultiPolygon in square kilometers.
 * This function handles complex polygons including those with holes.
 */
export const calculateArea = (multiPolygon: MultiPolygon): number => {
  // Convert degrees to radians for spherical calculations
  const toRadians = (coord: number) => (coord * Math.PI) / 180;
  // Earth's radius in kilometers, used for area calculation
  const R = 6371;

  /**
   * Helper function to calculate area of a single polygon ring
   * Uses the spherical excess formula for geodesic area calculation
   * @param coordinates - Array of coordinate pairs forming a polygon ring
   * @returns Area of the polygon ring in square kilometers
   */
  const polygonArea = (coordinates: Position[]): number => {
    let area = 0;

    if (coordinates.length > 2) {
      for (let i = 0; i < coordinates.length - 1; i++) {
        const p1 = coordinates[i];
        const p2 = coordinates[i + 1];
        // Convert latitude and longitude to radians
        const lat1 = toRadians(p1[1]);
        const lon1 = toRadians(p1[0]);
        const lat2 = toRadians(p2[1]);
        const lon2 = toRadians(p2[0]);

        // Apply spherical excess formula
        area += (lon2 - lon1) * (2 + Math.sin(lat1) + Math.sin(lat2));
      }
      // Scale area by Earth's radius squared and divide by 2
      area = (area * R * R) / 2;
    }
    return Math.abs(area);
  };

  // Calculate total area by processing each polygon in the MultiPolygon
  return multiPolygon.coordinates.reduce(
    (totalArea: number, polygon: Position[][]) => {
      // Calculate area of main polygon (exterior ring)
      const mainArea = polygonArea(polygon[0]);
      // Calculate sum of hole areas (interior rings)
      const holesArea = polygon
        .slice(1)
        .reduce((sum, hole) => sum + polygonArea(hole), 0);
      // Subtract hole areas from main polygon area
      return totalArea + (mainArea - holesArea);
    },
    0
  );
};

// TODO: Refactor this after API provide BBox
/**
 * Calculates map bounds from a GeoJSON feature collection
 */
export const calculateFeatureBounds = (
  features: FeatureCollection<Geometry>
): maplibregl.LngLatBounds => {
  const bounds = new LngLatBounds();

  const handlePoint = (coords: LngLatLike) => bounds.extend(coords);
  const handleLineString = (coords: number[][]) =>
    coords.forEach((coord) => handlePoint(coord as LngLatLike));
  const handlePolygon = (coords: number[][][]) =>
    coords.forEach(handleLineString);
  const handleMultiPolygon = (coords: number[][][][]) =>
    coords.forEach(handlePolygon);

  const handleGeometry = (geometry: Geometry) => {
    if (!geometry) return;
    switch (geometry.type) {
      case "Point":
        return handlePoint(geometry.coordinates as LngLatLike);
      case "LineString":
      case "MultiPoint":
        return handleLineString(geometry.coordinates);
      case "Polygon":
      case "MultiLineString":
        return handlePolygon(geometry.coordinates);
      case "MultiPolygon":
        return handleMultiPolygon(geometry.coordinates);
      case "GeometryCollection":
        return geometry.geometries.forEach(handleGeometry);
    }
  };
  features.features.forEach(({ geometry }) => handleGeometry(geometry));
  return bounds;
};

/**
 * Validates if coordinates are within valid latitude and longitude bounds
 */
export const isValidCoordinates = (point: Position): boolean => {
  return (
    Array.isArray(point) &&
    point.length === 2 &&
    typeof point[0] === "number" &&
    typeof point[1] === "number" &&
    point[0] >= -180 &&
    point[0] <= 180 && // Valid longitude range
    point[1] >= -90 &&
    point[1] <= 90 // Valid latitude range
  );
};

/**
 * Type guard to validate if the provided data is a valid MultiPolygon Feature
 * Checks structure and coordinate bounds
 */
export const isValidMultiPolygon = (
  data: unknown
): data is Feature<MultiPolygon> => {
  if (!data || typeof data !== "object") return false;

  // Cast to expected structure for type checking
  const feature = data as {
    type?: unknown;
    properties?: unknown;
    geometry?: {
      type?: unknown;
      coordinates?: unknown;
    };
  };

  // Validate Feature structure
  if (feature.type !== "Feature") return false;
  if (!feature.properties || typeof feature.properties !== "object")
    return false;
  if (!feature.geometry || typeof feature.geometry !== "object") return false;
  if (feature.geometry.type !== "MultiPolygon") return false;

  const coordinates = feature.geometry.coordinates;
  if (!Array.isArray(coordinates)) return false;

  // Type definitions for coordinate structure
  type LinearRing = Position[];
  type Polygon = LinearRing[];

  // Validate coordinate structure and bounds
  return coordinates.every(
    (polygon: Polygon) =>
      Array.isArray(polygon) &&
      polygon.every(
        (ring: LinearRing) =>
          Array.isArray(ring) &&
          ring.every((point: Position) => isValidCoordinates(point))
      )
  );
};

/**
 * Type guard to validate if the provided data is a valid Point FeatureCollection
 * Checks structure and coordinate bounds of each point
 * @param data - Unknown data to validate
 * @returns Boolean indicating if data is a valid Point FeatureCollection
 */
export const isValidPointsCollection = (
  data: unknown
): data is FeatureCollection<Point> => {
  if (!data || typeof data !== "object") return false;

  // Cast to expected structure for type checking
  const collection = data as {
    type?: unknown;
    features?: Array<{
      type?: unknown;
      properties?: unknown;
      geometry?: {
        type?: unknown;
        coordinates?: unknown;
      };
    }>;
  };

  // Validate FeatureCollection structure
  if (collection.type !== "FeatureCollection") return false;
  if (!Array.isArray(collection.features)) return false;

  // Remove all features without geometry
  const collectionsHasGeomValue = collection.features.filter(
    (feature) => feature?.geometry?.type === "Point"
  );
  // Validate each feature in the collection
  return collectionsHasGeomValue.every((feature) => {
    if (!feature || typeof feature !== "object") return false;
    if (feature.type !== "Feature") return false;
    if (!feature.properties || typeof feature.properties !== "object")
      return false;
    if (!feature.geometry || typeof feature.geometry !== "object") return false;

    const coordinates = feature.geometry.coordinates;
    if (!Array.isArray(coordinates)) return false;
    if (coordinates.length !== 2) return false;

    // Use the new isValidCoordinates function
    return isValidCoordinates(coordinates);
  });
};

/**
 * Validates if the points data meets all requirements for creating a cluster layer
 */
export const isValidPointsData = (
  map: MaplibreMap | null,
  pointsData: FeatureCollection<Geometry>
): boolean => {
  return !!(
    map &&
    pointsData &&
    pointsData.features.length > 0 &&
    isValidPointsCollection(pointsData)
  );
};

export const isCategoryRange = (range: unknown): range is CategoryRange => {
  return (
    typeof range === "object" &&
    range !== null &&
    "category" in range &&
    "color" in range
  );
};

export const isAutoRange = (
  range: unknown
): range is { min: number; max: number } => {
  return (
    typeof range === "object" &&
    range !== null &&
    "min" in range &&
    "max" in range
  );
};

export const generateColorRanges = (
  ranges: (number | null)[] | CategoryColorCategory[],
  colorFamily: EColorNames,
  data_type: DataSchemaType,
  step: number
): ColorRanges[] => {
  if (isOrdinalType(data_type)) {
    return generateCategoryColorRanges(
      ranges as CategoryRange[],
      colorFamily,
      step
    );
  }
  return generateAutoRangeColorRanges(
    ranges as (number | null)[],
    colorFamily,
    data_type,
    step || 10
  );
};

/**
 * Generates color ranges for a given ordinal ranges and color family
 */
export const generateCategoryColorRanges = (
  ranges: CategoryRange[],
  colorFamily: EColorNames,
  step: number
): ColorRanges[] => {
  return ranges.map((range) => {
    if (!range.category) {
      return {
        value: "",
        color: "#D3D3D3",
        label: "null"
      };
    }
    return {
      value: range.category.toString(),
      color:
        COLORS[colorFamily][getColorScaleMap(colorFamily, step)[range.color]],
      label: range.category
    };
  });
};

/**
 * Generates color ranges for a given set of quantiles and color family
 * This function creates a series of min-max range objects with associated colors
 * for visualizing numeric data using color gradients
 *
 * @param quantiles - Array of breakpoints from handleNumericValues (sorted in ascending order)
 * @param colorFamily - The color family to use for the gradient
 * @returns Array of range objects with min, max, color, and label properties
 */
export const generateAutoRangeColorRanges = (
  quantiles: (number | null)[],
  colorFamily: EColorNames,
  selectedCategory: DataSchemaType,
  step: number = 10
): ColorRanges[] => {
  // Handle empty or invalid input
  if (!quantiles || !quantiles.length) {
    return [];
  }

  const isFloat = selectedCategory === DataSchemaType.FLOAT;
  const formatType = isFloat ? "decimal" : "integer";
  const DECIMALS = FORMAT_CONFIG.quantileBreaks;
  const hasNull = quantiles.includes(null);
  const numericQuantiles = quantiles.filter((q): q is number => q !== null);

  const nullData = {
    min: null,
    max: null,
    value: "null",
    color: "#D3D3D3",
    label: "null"
  };

  const uniqueQuantiles = [
    ...new Set(numericQuantiles.filter((quantile) => quantile != null))
  ];

  if (uniqueQuantiles.length === 0) {
    return hasNull ? [nullData] : [];
  }

  if (uniqueQuantiles.length <= step) {
    const colorRanges: {
      min: number | null;
      max: number | null;
      value?: string;
      color: string;
      label: string;
    }[] = uniqueQuantiles.map((rawMin, index) => {
      const min = roundUpDecimal(Number(rawMin), DECIMALS);
      return {
        min,
        max: min,
        color: String(
          COLORS[colorFamily][getColorScaleMap(colorFamily, step)[index]]
        ),
        label: `${formatNumber(min, formatType, { maximumFractionDigits: DECIMALS })}`
      };
    });

    if (hasNull) colorRanges.push(nullData);

    return colorRanges;
  }

  // Create color ranges from the descending quantiles
  // Each range consists of [current, next] value as [min, max]
  const colorRanges: {
    min: number | null;
    max: number | null;
    value?: string;
    color: string;
    label: string;
  }[] = uniqueQuantiles.slice(0, -1).map((rawMax, index) => {
    const nextQuantile = uniqueQuantiles[index + 1];
    /**
     * Calculates the lower bound (`min`) for each data range.
     * For all but the very last range, `min` is derived by adding value to the upper bound of the subsequent (lower) quantile range, ensuring no overlap.
     * For the final (smallest) range, `min` is determined by subtracting value nearest from its own `max` value, acting as a sensible fallback.
     */
    const isLastRange = index === uniqueQuantiles.length - 2;

    const max = roundUpDecimal(Number(rawMax), DECIMALS);

    /**
     * `nextFormatted` is the value rounded up to 3 decimal places and converted to a string (e.g. "1.820").
     * This preserves trailing zeros, ensuring precise stepping when calculating the next safe number.
     *
     * For example: 1.8192 becomes 1.82 after `roundUpDecimal`. If passed directly to `getNextNumberSafeFormatted`,
     * it would be treated as "1.82" → and the result would be 1.83, which creates too large a gap.
     *
     * By converting to "1.820" (string), we preserve the intent of 3 visible decimals, and `getNextNumberSafeFormatted`
     * will return 1.821 — a much smaller and more accurate step.
     */
    const nextFormatted = roundUpDecimal(
      Number(nextQuantile),
      DECIMALS
    ).toFixed(DECIMALS);

    const min = isLastRange
      ? nextQuantile
      : getNextNumberSafeFormatted(nextFormatted, formatType);

    return {
      min,
      max,
      color: String(
        COLORS[colorFamily][getColorScaleMap(colorFamily, step)[index]]
      ),
      // Explicit DECIMALS, not the module default: these boundaries are
      // computed at that precision, and labelling them at fewer makes
      // adjacent ranges print identical text.
      label: `${formatNumber(min, formatType, { maximumFractionDigits: DECIMALS })} - ${formatNumber(max, formatType, { maximumFractionDigits: DECIMALS })}`
    };
  });

  if (hasNull) {
    colorRanges.push({
      min: null,
      max: null,
      value: "null",
      color: "#D3D3D3",
      label: "null"
    });
  }

  return colorRanges;
};

// Create an array of unique breakpoints and their colors
export const generateColorRangesUniqueStops = (
  colorRanges: ColorRanges[]
): [number | null, string][] => {
  // 1. Separate null ranges from numeric ranges to avoid null→0 collision
  const nullRange = colorRanges.find((range) => range.min === null);
  const numericRanges = colorRanges.filter(
    (range): range is ColorRanges & { min: number } => range.min !== null
  );

  // 2. Build unique numeric stops using the color already assigned in range.color
  const uniqueNumericStops = numericRanges
    .reduce((acc: [number, string][], range) => {
      if (!acc.some(([value]) => value === range.min)) {
        acc.push([range.min, range.color]);
      }
      return acc;
    }, [])
    .sort(([a], [b]) => a - b);

  // 3. Prepend null entry so it becomes the step-expression default
  const nullColor = nullRange?.color ?? "#CECECE";
  return [[null, nullColor], ...uniqueNumericStops];
};

export const generateDatasetId = (
  datasetId: string,
  datasetIndices: {
    [baseId: string]: {
      counter: number;
      usedIndices: number[];
    };
  }
) => {
  const currentIndices = datasetIndices?.[datasetId]?.usedIndices || [];
  let nextIndex = 1;

  while (currentIndices.includes(nextIndex)) nextIndex++;

  const newId = `${datasetId}_${nextIndex}`;

  return { id: newId, nextIndex };
};
/**
 * Flattens a deeply nested object into a single-level object with dot-notated keys.
 */
export const flattenObject = (
  obj: Record<string, unknown>,
  prefix = ""
): Record<string, unknown> => {
  const result: Record<string, unknown> = {};

  for (const key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      const value = obj[key];
      const fullKey = prefix ? `${prefix}.${key}` : key;

      if (value === null || typeof value !== "object" || Array.isArray(value)) {
        result[fullKey] = value;
      } else {
        const nested = flattenObject(value as Record<string, unknown>, fullKey);
        Object.assign(result, nested);
      }
    }
  }

  return result;
};

/**
 * Checks if any of the provided deeply nested objects differ from each other.
 *
 * - Works for any number of JSON-like objects.
 * - Supports deep comparison.
 * - Ignores key order.
 *
 * @param objects - A list of plain objects to compare
 * @returns `true` if at least one object differs from the rest
 */
export const areObjectsDifferentDeeply = <T extends Record<string, unknown>>(
  ...objects: T[]
): boolean => {
  if (objects.length < 2) return false;

  const flatObjects = objects.map((obj) => flattenObject(obj));
  const base = flatObjects[0];
  const allKeys = new Set(flatObjects.flatMap((obj) => Object.keys(obj)));

  for (const key of allKeys) {
    const baseValue = base[key];

    for (let i = 1; i < flatObjects.length; i++) {
      if (flatObjects[i][key] !== baseValue) {
        return true;
      }
    }
  }

  return false;
};

export const isPrimitiveArrayValue = (
  value: unknown
): value is PrimitiveDisplayValue[] =>
  Array.isArray(value) &&
  value.length > 0 &&
  value.every(
    (item) =>
      item === null ||
      item === undefined ||
      typeof item === "string" ||
      typeof item === "number" ||
      typeof item === "boolean"
  );

export const isObjectArrayValue = (
  value: unknown
): value is (Record<string, unknown> | string)[] =>
  Array.isArray(value) &&
  value.length > 0 &&
  value.every(
    (item) =>
      (typeof item === "object" && item !== null && !Array.isArray(item)) ||
      typeof item === "string"
  );
